/**
 * Client-side toast message allowlist — plan 22.
 *
 * The server's global error handler emits responses shaped
 * `{ error: { code, message }, requestId? }` (see
 * `src/server/middleware/errorHandler.ts`). The `message` field is curated
 * for domain errors BUT it still carries server-authored copy that should
 * not land verbatim in the UI — translations, tone drift, and the
 * `devMessage` debug leak in non-prod builds are all potential footguns.
 *
 * `messageFor(code)` projects a DomainErrorCode (plus a few client-only
 * codes) to a short, user-facing string. Anything not in the allowlist
 * falls through to the caller-supplied fallback, defaulting to a generic
 * "Something went wrong." — we explicitly NEVER return the raw
 * `error.message` from the wire.
 *
 * Call-sites that previously surfaced `err.message` in a toast should pass
 * `messageFor(response.error?.code)` instead. Where the caller still wants
 * to differentiate by a specific error shape it owns, add the code here —
 * do not branch on message substrings.
 */

/**
 * Every code we are prepared to show the user. Keep this list curated; an
 * unknown code is a signal the UI does not have tested copy for that path.
 */
export type AllowedErrorCode =
  // Shared DomainErrorCode set (keep in sync with src/shared/errors/index.ts)
  | 'validation_error'
  | 'not_found'
  | 'forbidden'
  | 'unauthorized'
  | 'payment_error'
  | 'rate_limited'
  | 'conflict'
  | 'internal_error'
  // Client-specific codes emitted by middleware that doesn't route through
  // the DomainError stack (CSRF shim, auth middleware, invalid-login path).
  | 'invalid_credentials'
  | 'account_not_found'
  | 'invalid_verification_code'
  | 'email_in_use'
  | 'csrf_error'
  | 'network_error'
  | 'server_error';

const MESSAGES: Record<AllowedErrorCode, string> = {
  validation_error: 'Please check the form and try again.',
  // Since the owner decision of 2026-07-10 the server distinguishes "no
  // such account" (account_not_found — the AuthModal renders a guided
  // panel for it, this copy is the non-modal fallback) from "wrong
  // password" (invalid_credentials).
  invalid_credentials:
    "That password doesn't match. Try again, or use “Forgot password?” to reset it.",
  account_not_found: "We couldn't find an account with that email address.",
  invalid_verification_code:
    "That verification code didn't match — request a new one and try again.",
  email_in_use: 'That email is already registered. Try signing in instead.',
  rate_limited: 'Too many attempts. Please wait a minute and try again.',
  forbidden: "You don't have permission to do that.",
  unauthorized: 'Please sign in to continue.',
  not_found: 'We couldn’t find what you’re looking for.',
  payment_error: 'Payment could not be completed. Please try another method.',
  conflict: 'That action conflicts with existing data. Please refresh and retry.',
  internal_error: 'Something went wrong on our side. Please try again.',
  csrf_error: 'Your session expired. Please refresh the page and try again.',
  network_error: "Couldn't reach the server. Check your connection and try again.",
  server_error: "We're having trouble on our end. Please try again in a moment.",
};

/**
 * Resolve a user-facing message for an error `code`. Returns `fallback` when
 * the code is absent or not in the allowlist.
 *
 * Usage:
 *   try {
 *     await safeFetch(...);
 *   } catch (err) {
 *     const code = extractErrorCode(err);
 *     toast(messageFor(code, 'Could not save changes.'));
 *   }
 */
export function messageFor(
  code: string | undefined | null,
  fallback: string = 'Something went wrong.'
): string {
  if (!code) return fallback;
  if (code in MESSAGES) return MESSAGES[code as AllowedErrorCode];
  return fallback;
}

/**
 * Extract the first zod issue from a `validation_error` response body. The
 * server's `validate()` middleware emits `{ error, code: 'validation_error',
 * issues: [{ path, message }] }`; this helper surfaces the most actionable
 * issue (the first one) as `"<path>: <message>"` so the UI can replace the
 * generic "Please check the form" copy with something the user can act on.
 *
 * Returns `undefined` if there are no issues to surface, leaving the caller
 * to fall back to the curated `messageFor('validation_error')` copy.
 */
export function extractFirstValidationIssue(input: unknown): string | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const obj = input as { payload?: unknown; issues?: unknown };
  const payload = obj.payload && typeof obj.payload === 'object' ? obj.payload : obj;
  const issues = (payload as { issues?: unknown }).issues;
  if (!Array.isArray(issues) || issues.length === 0) return undefined;
  const first = issues[0] as { path?: unknown; message?: unknown };
  const message = typeof first.message === 'string' ? first.message : undefined;
  if (!message) return undefined;
  const path = typeof first.path === 'string' && first.path.length > 0 ? first.path : undefined;
  return path ? `${path}: ${message}` : message;
}

/**
 * Extract a `code` from a thrown error or an API response body. `safeFetch`
 * attaches the parsed JSON body to `err.payload` on non-2xx responses;
 * callers may also pass the decoded body directly.
 */
export function extractErrorCode(input: unknown): string | undefined {
  if (!input) return undefined;
  if (typeof input === 'object') {
    const obj = input as { code?: unknown; error?: unknown; payload?: unknown };
    if (typeof obj.code === 'string') return obj.code;
    // `{ error: { code, message } }`
    if (obj.error && typeof obj.error === 'object') {
      const inner = obj.error as { code?: unknown };
      if (typeof inner.code === 'string') return inner.code;
    }
    // safeFetch's thrown Error decorates `payload` with the parsed body.
    if (obj.payload && typeof obj.payload === 'object') {
      return extractErrorCode(obj.payload);
    }
  }
  return undefined;
}

/**
 * Single entry point for "what should the toast/banner say given this thrown
 * error?" — replaces the older `messageFor(extractErrorCode(err), fallback)`
 * dance at every call site. Resolution order, first-match-wins:
 *
 *   1. Network / offline (safeFetch throws ApiError with status=0 for
 *      `Failed to fetch`). Map to `network_error` so the user sees a retry
 *      hint instead of a server-side message that isn't actually true.
 *   2. 5xx responses. Map to `server_error` regardless of any code the
 *      server attached — the user shouldn't see "validation_error" copy
 *      when the real cause is the API panicking.
 *   3. `validation_error` with a populated `issues[]` — surface the first
 *      field-level message so the user can act on it
 *      (e.g. "password: String must contain at least 8 character(s)").
 *   4. Any code in the `messageFor` allowlist — render its curated copy.
 *   5. Fall back to the caller's domain-specific string ("Sign in failed.",
 *      "Could not save changes.", etc.).
 *
 * The status check intentionally peeks at `err.status` directly so this
 * works without importing `ApiError` (avoids a layering dependency from
 * `shared/errors` into `lib/utils`).
 */
export function userMessageForError(err: unknown, fallback: string): string {
  const status =
    err && typeof err === 'object' && typeof (err as { status?: unknown }).status === 'number'
      ? (err as { status: number }).status
      : undefined;

  if (status === 0) return MESSAGES.network_error;
  if (typeof status === 'number' && status >= 500 && status < 600) {
    return MESSAGES.server_error;
  }

  const code = extractErrorCode(err);

  if (code === 'validation_error') {
    const issue = extractFirstValidationIssue(err);
    if (issue) return issue;
  }

  return messageFor(code, fallback);
}
